import { createHash } from 'crypto'
import fs from 'fs'
import path from 'path'

import { HASH_EXCLUDED_FOLDERS } from '../../constants'

// The version of a service is a hash of the service, and of nothing else.
//
// That is what makes it comparable as well as unique: two checkouts of the same service hash the same
// wherever they live, so `pull` can tell "already up to date" from "differs" without downloading anything —
// and a change to a sibling service, or to the project around it, cannot move it.
//
// `node_modules` is packed but not hashed. The service's own `package-lock.json` is hashed and says
// everything about the dependency tree that matters, so a bump still produces a new version — which it must,
// since the pod caches by this hash and the tree ships inside the package.
//
// Paths stay relative to the service root: the same sources unpacked anywhere else must hash the same.

export async function computeServiceHash(projectRoot: string, serviceId: string): Promise<string> {
  const hash = createHash('sha256')
  const serviceDir = path.resolve(projectRoot, 'services', serviceId)

  const files: string[] = []

  collectFiles(serviceDir, '', files)
  files.sort()

  for (const file of files) {
    hash.update(file)
    hash.update(fs.readFileSync(path.resolve(serviceDir, file)))
  }

  return hash.digest('hex')
}

/** Walks `dir` and appends every file to `files`, relative to the service root. */
function collectFiles(serviceDir: string, relativeDir: string, files: string[]): void {
  const entries = fs.readdirSync(path.resolve(serviceDir, relativeDir), { withFileTypes: true })

  for (const entry of entries) {
    if (HASH_EXCLUDED_FOLDERS.includes(entry.name)) {
      continue
    }

    const relativePath = relativeDir ? path.join(relativeDir, entry.name) : entry.name

    if (!entry.isSymbolicLink()) {
      if (entry.isDirectory()) {
        collectFiles(serviceDir, relativePath, files)
      } else {
        files.push(relativePath)
      }
    }
  }
}
